import { useCallback, useMemo, useState } from "react";
import {
  ListRestart,
  MoreVertical,
  Pause,
  Play,
  Plus,
  RefreshCw,
  ShieldOff,
} from "lucide-react";

import { PageHeader } from "@/components/shared/page-header";

import {
  ProvidersService,
  type CredentialStatus,
  type ProviderCredentialListResponse,
  type ProviderCredentialResponse,
  type ProviderType,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatRelativeTimeID } from "@/lib/format";
import {
  CREDENTIAL_STATUS_TONE,
  PROVIDER_TYPE_LABEL,
  formatMaskedKey,
  formatProviderLabel,
} from "@/lib/provider-helpers";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FilterChips } from "@/components/shared/filter-chip";
import { Pagination } from "@/components/shared/pagination";
import { Section } from "@/components/shared/section";
import { ProviderCreateDialog } from "@/components/settings/provider-create-dialog";
import { ProviderRevokeDialog } from "@/components/settings/provider-revoke-dialog";
import { ProviderRotateDialog } from "@/components/settings/provider-rotate-dialog";
import { ProviderShowOnceDialog } from "@/components/settings/provider-show-once-dialog";

const PAGE_SIZE = 25;
const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const PROVIDER_FILTER_OPTIONS: ReadonlyArray<{
  value: ProviderType;
  label: string;
}> = (["openai", "anthropic", "bedrock"] as const).map((p) => ({
  value: p,
  label: PROVIDER_TYPE_LABEL[p],
}));

// Credential lifecycle states map to translation keys so the
// status badge + filter chips re-render on a locale switch.
const CREDENTIAL_STATUS_KEY: Record<CredentialStatus, TranslationKey> = {
  active: "providers.status.active",
  disabled: "providers.status.disabled",
  revoked: "providers.status.revoked",
};

export default function ProvidersPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const statusFilterOptions = useMemo(
    () =>
      (["active", "disabled", "revoked"] as const).map((s) => ({
        value: s,
        label: t(CREDENTIAL_STATUS_KEY[s]),
      })),
    [t]
  );

  const [providerFilter, setProviderFilter] = useState<ProviderType | null>(
    null
  );
  const [statusFilter, setStatusFilter] = useState<CredentialStatus | null>(
    null
  );
  const [page, setPage] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [showOnce, setShowOnce] = useState<{
    credential: ProviderCredentialResponse;
    plaintext: string;
    variant: "created" | "rotated";
  } | null>(null);
  const [rotateTarget, setRotateTarget] =
    useState<ProviderCredentialResponse | null>(null);
  const [revokeTarget, setRevokeTarget] =
    useState<ProviderCredentialResponse | null>(null);

  const offset = page * PAGE_SIZE;
  const fetcher = useCallback(() => {
    const filters: {
      provider_type?: ProviderType;
      status?: CredentialStatus;
      limit: number;
      offset: number;
    } = { limit: PAGE_SIZE, offset };
    if (providerFilter) filters.provider_type = providerFilter;
    if (statusFilter) filters.status = statusFilter;
    return ProvidersService.list(filters);
  }, [providerFilter, statusFilter, offset]);

  const { data, error, isLoading, refetch } =
    useApi<ProviderCredentialListResponse>(fetcher, {
      deps: [providerFilter, statusFilter, offset],
    });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;
  const showEmptyState =
    !isLoading &&
    items.length === 0 &&
    providerFilter === null &&
    statusFilter === null;

  const handleProviderFilter = (next: ProviderType | null) => {
    setProviderFilter(next);
    setPage(0);
  };
  const handleStatusFilter = (next: CredentialStatus | null) => {
    setStatusFilter(next);
    setPage(0);
  };

  const toggle = useMutation<
    { id: string; next: "active" | "disabled" },
    ProviderCredentialResponse
  >(
    ({ id, next }) => ProvidersService.update(id, { status: next }),
    { onSuccess: () => void refetch() }
  );

  const [refreshedName, setRefreshedName] = useState<string | null>(null);
  const refreshModels = useMutation<string, ProviderCredentialResponse>(
    (id) => ProvidersService.refreshModels(id),
    {
      onSuccess: (cred) => {
        setRefreshedName(cred.credential_name);
        void refetch();
      },
    }
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("providers.page.title")}
        description={t("providers.page.description")}
      />
      {!canManage && (
        <Alert variant="info">{t("providers.page.readOnlyNotice")}</Alert>
      )}

      <Section
        title={t("providers.page.sectionTitle")}
        description={t("providers.page.sectionDescription")}
        action={
          canManage && !showEmptyState ? (
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus size={14} />
              {t("providers.actions.add")}
            </Button>
          ) : undefined
        }
      >
        {error && <Alert variant="warning">{parseApiError(error)}</Alert>}
        {toggle.error && (
          <Alert variant="danger">{parseApiError(toggle.error)}</Alert>
        )}
        {refreshModels.error && (
          <Alert variant="danger">{parseApiError(refreshModels.error)}</Alert>
        )}
        {refreshedName && (
          <Alert variant="success">
            {t("providers.actions.refreshModelsSuccess", {
              name: refreshedName,
            })}
          </Alert>
        )}

        {showEmptyState ? (
          <EmptyState
            canManage={canManage}
            onCreateClick={() => setCreateOpen(true)}
          />
        ) : (
          <>
            <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
              <FilterChips
                label={t("providers.filters.provider")}
                options={PROVIDER_FILTER_OPTIONS}
                value={providerFilter}
                onChange={handleProviderFilter}
              />
              <FilterChips
                label={t("providers.filters.status")}
                options={statusFilterOptions}
                value={statusFilter}
                onChange={handleStatusFilter}
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("providers.table.name")}</TableHead>
                    <TableHead>{t("providers.table.provider")}</TableHead>
                    <TableHead>{t("providers.table.key")}</TableHead>
                    <TableHead>{t("providers.table.status")}</TableHead>
                    <TableHead className="hidden tablet:table-cell">
                      {t("providers.table.created")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("providers.table.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && items.length === 0 && (
                    <>
                      {[0, 1, 2].map((i) => (
                        <TableRow key={`prov-sk-${i}`}>
                          <TableCell colSpan={6} className="py-4">
                            <Skeleton className="h-5 w-full" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  {!isLoading && items.length === 0 && (
                    <TableEmpty
                      colSpan={6}
                      message={t("providers.table.emptyFiltered")}
                    />
                  )}
                  {items.map((cred) => (
                    <TableRow key={cred.id}>
                      <TableCell className="font-medium text-ocean-900">
                        {cred.credential_name}
                      </TableCell>
                      <TableCell className="text-neutral-700">
                        {formatProviderLabel(cred.provider_type)}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs text-ocean-700">
                          {formatMaskedKey(
                            cred.provider_type,
                            cred.api_key_last_4
                          )}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge tone={CREDENTIAL_STATUS_TONE[cred.status]}>
                          {t(CREDENTIAL_STATUS_KEY[cred.status])}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden tablet:table-cell text-xs text-neutral-600">
                        {formatRelativeTimeID(cred.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canManage && cred.status !== "revoked" && (
                          <CredentialActions
                            credential={cred}
                            disableInFlight={toggle.isLoading}
                            refreshInFlight={refreshModels.isLoading}
                            onRotate={() => setRotateTarget(cred)}
                            onToggle={(next) =>
                              void toggle.mutate({ id: cred.id, next })
                            }
                            onRefreshModels={() => {
                              setRefreshedName(null);
                              void refreshModels.mutate(cred.id);
                            }}
                            onRevoke={() => setRevokeTarget(cred)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {items.length > 0 && (
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                hasNext={hasNext}
                onPrev={() => setPage((p) => Math.max(0, p - 1))}
                onNext={() => setPage((p) => p + 1)}
              />
            )}
          </>
        )}
      </Section>

      <ProviderCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={({ credential, plaintext }) => {
          setCreateOpen(false);
          if (plaintext) {
            setShowOnce({ credential, plaintext, variant: "created" });
          }
          void refetch();
        }}
      />

      <ProviderRotateDialog
        credential={rotateTarget}
        onClose={() => setRotateTarget(null)}
        onRotated={({ credential, plaintext }) => {
          setRotateTarget(null);
          setShowOnce({ credential, plaintext, variant: "rotated" });
          void refetch();
        }}
      />

      <ProviderRevokeDialog
        credential={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={() => {
          setRevokeTarget(null);
          void refetch();
        }}
      />

      <ProviderShowOnceDialog
        data={
          showOnce
            ? { credential: showOnce.credential, plaintext: showOnce.plaintext }
            : null
        }
        variant={showOnce?.variant ?? "created"}
        onClose={() => setShowOnce(null)}
      />
    </div>
  );
}

function CredentialActions({
  credential,
  disableInFlight,
  refreshInFlight,
  onRotate,
  onToggle,
  onRefreshModels,
  onRevoke,
}: {
  credential: ProviderCredentialResponse;
  disableInFlight: boolean;
  refreshInFlight: boolean;
  onRotate: () => void;
  onToggle: (next: "active" | "disabled") => void;
  onRefreshModels: () => void;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  const isActive = credential.status === "active";
  const nextToggleState: "active" | "disabled" = isActive
    ? "disabled"
    : "active";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("providers.actions.rowAriaLabel", {
            name: credential.credential_name,
          })}
        >
          <MoreVertical size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="right">
        <DropdownMenuItem
          onClick={onRefreshModels}
          disabled={refreshInFlight || !isActive}
        >
          <ListRestart size={14} />
          {t("providers.actions.refreshModels")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRotate}>
          <RefreshCw size={14} />
          {t("providers.actions.rotate")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onToggle(nextToggleState)}
          disabled={disableInFlight}
        >
          {isActive ? (
            <>
              <Pause size={14} />
              {t("providers.actions.disable")}
            </>
          ) : (
            <>
              <Play size={14} />
              {t("providers.actions.enable")}
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onRevoke} variant="danger">
          <ShieldOff size={14} />
          {t("providers.actions.revoke")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyState({
  canManage,
  onCreateClick,
}: {
  canManage: boolean;
  onCreateClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 p-5 desktop:p-7">
      <h2 className="text-lg font-medium text-ocean-900">
        {t("providers.emptyState.title")}
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-neutral-600">
        {t("providers.emptyState.description")}
      </p>

      <div className="mt-5 space-y-3">
        <QuickstartStep index={1} title={t("providers.emptyState.step1Title")}>
          {t("providers.emptyState.step1Body")}
        </QuickstartStep>
        <QuickstartStep index={2} title={t("providers.emptyState.step2Title")}>
          {t("providers.emptyState.step2Body")}
        </QuickstartStep>
        <QuickstartStep index={3} title={t("providers.emptyState.step3Title")}>
          {t("providers.emptyState.step3Body")}
        </QuickstartStep>
      </div>

      {canManage && (
        <div className="mt-5">
          <Button onClick={onCreateClick}>
            <Plus size={14} />
            {t("providers.actions.add")}
          </Button>
        </div>
      )}
    </div>
  );
}

function QuickstartStep({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ocean-100 text-sm font-medium text-ocean-700">
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ocean-900">{title}</div>
          <div className="mt-1 text-xs text-neutral-600">{children}</div>
        </div>
      </div>
    </div>
  );
}
